<template>
  <div class="product-grid">
    <div v-if="editMode" class="edit-controls-wrapper">
      <div class="edit-toolbar">
        <div class="toolbar-content">
          <!-- Grid Dimensions - First -->
          <div class="toolbar-section">
            <span class="section-label">Dimenzije:</span>
            <v-select
              :items="rowColOptions"
              v-model="internalRowCount"
              label="Redaka"
              dense
              outlined
              hide-details
              class="filter-select dimension-select"
              @change="updateGridDimensions"
            >
            </v-select>
            <v-select
              :items="rowColOptions"
              v-model="internalColCount"
              label="Stupaca"
              dense
              outlined
              hide-details
              class="filter-select dimension-select"
              @change="updateGridDimensions"
            >
            </v-select>
          </div>

          <v-divider vertical class="toolbar-divider"></v-divider>

          <!-- Cell Actions -->
          <div class="toolbar-section">
            <span class="section-label">Akcije:</span>
            <v-btn
              class="edit-toolbar-btn"
              :disabled="selectedCells.length !== 1"
              @click="editCell"
              small
            >
              <v-icon small class="mr-1">mdi-pencil</v-icon>
              Uredi
            </v-btn>
            <v-btn
              class="edit-toolbar-btn"
              :disabled="!canMerge"
              @click="mergeCells"
              small
            >
              <v-icon small class="mr-1">mdi-table-merge-cells</v-icon>
              Spoji
            </v-btn>
            <v-btn
              class="edit-toolbar-btn"
              :disabled="!canUnmerge"
              @click="unmergeCell"
              small
            >
              <v-icon small class="mr-1">mdi-table-split-cell</v-icon>
              Razdvoji
            </v-btn>
            <v-btn
              class="edit-toolbar-btn-danger"
              :disabled="!canDelete"
              @click="deleteCell"
              small
            >
              <v-icon small class="mr-1">mdi-delete</v-icon>
              Ukloni
            </v-btn>
          </div>

          <v-divider vertical class="toolbar-divider"></v-divider>

          <!-- Layout Management -->
          <div class="toolbar-section">
            <span class="section-label">Prikazi:</span>
            <v-select
              :items="savedLayouts"
              item-text="name"
              item-value="id"
              v-model="selectedLayoutId"
              label="Odaberi prikaz"
              dense
              outlined
              hide-details
              clearable
              class="filter-select layout-select"
            >
            </v-select>
            <v-btn
              class="edit-toolbar-btn"
              @click="loadLayout"
              :disabled="!selectedLayoutId"
              small
            >
              <v-icon small class="mr-1">mdi-download</v-icon>
              Učitaj
            </v-btn>
            <v-btn
              class="edit-toolbar-btn"
              @click="saveLayoutAs"
              small
            >
              <v-icon small class="mr-1">mdi-content-save-plus</v-icon>
              Spremi kao
            </v-btn>
            <v-btn
              class="edit-toolbar-btn-danger"
              @click="deleteLayout"
              :disabled="!selectedLayoutId"
              small
            >
              <v-icon small class="mr-1">mdi-delete</v-icon>
              Obriši
            </v-btn>
          </div>

          <div class="toolbar-spacer"></div>

          <!-- Save and Exit -->
          <div class="toolbar-section">
            <v-btn
              class="edit-toolbar-btn-primary"
              @click="saveAndExitEditMode"
              small
            >
              <v-icon small class="mr-1">mdi-check</v-icon>
              Spremi i zatvori
            </v-btn>
          </div>
        </div>
      </div>
    </div>
    <v-progress-linear v-if="!loaded" indeterminate></v-progress-linear>
    <div
      v-if="loaded"
      class="product-grid-container"
      :style="{
        'grid-template-columns': `repeat(${colCount}, 1fr)`,
        'grid-template-rows': `repeat(${rowCount}, 1fr)`,
      }"
    >
      <template v-for="(row, idxr) in grid">
        <template v-for="(item, idxc) in row">
          <div
            v-if="!isCellHidden(idxr, idxc) && item"
            :key="`cell-${idxr}-${idxc}`"
            class="panel-cell"
            :class="{ 'selected-cell': isCellSelected(item) }"
            :style="{
              'grid-column': `span ${item.colspan || 1}`,
              'grid-row': `span ${item.rowspan || 1}`,
            }"
            @click="onCellClick(item)"
          >
            <v-btn
              :class="{
                'tile-glass-effect': item.hasGlassEffect,
                'tile-3d-effect': item.has3DEffect,
                'tile-neon-effect': item.hasNeonEffect,
                'tile-pulse-effect': item.hasPulseEffect,
                'tile-neumorphism-effect': item.hasNeumorphismEffect,
                'tile-gradient-border-effect': item.hasGradientBorderEffect,
                'tile-shimmer-effect': item.hasShimmerEffect,
                'tile-floating-effect': item.hasFloatingEffect,
                'tile-shadow-lift-effect': item.hasShadowLiftEffect,
                'tile-holographic-effect': item.hasHolographicEffect,
                'tile-frost-effect': item.hasFrostEffect,
                'tile-border-glow-effect': item.hasBorderGlowEffect,
                'tile-breathing-effect': item.hasBreathingEffect
              }"
              :style="{
                display: 'inline-block',
                borderRadius: '12px',
                position: 'relative',
                background: `${item.color || '#B8D4F1'} !important`
              }"
              :disabled="!editMode && item.id === ''"
              block
              tile
              depressed
              min-height="3.5rem"
              width="100%"
              height="100%"
            >
              <product-tile
                :color="item.foregroundColor"
                :label="item.label"
              ></product-tile>
              <v-chip
                v-if="
                  !editMode &&
                  item.active &&
                  getItemShortcut(item) &&
                  keyboardShortcutsEnabled &&
                  (showShortcutBadges || showBadgesTemporarily)
                "
                x-small
                color="white"
                text-color="primary"
                style="
                  position: absolute;
                  bottom: 4px;
                  right: 4px;
                  font-weight: bold;
                  min-width: 20px;
                "
              >
                {{ getItemShortcut(item) }}
              </v-chip>
            </v-btn>
          </div>
        </template>
      </template>
    </div>

    <!-- Message Box for confirmations and notifications -->
    <MsgBox ref="msgBox" @ok="confirmDialogAction" @cancel="onConfirmCancel" />

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="3000"
      :color="snackbar.color"
      top
      right
    >
      <v-icon left color="white">{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import ProductTile from '@/modules/cash-register/components/ProductTile'
import MsgBox from '@/components/MsgBox'
import { df } from '@/plugins/firebase'
import state from '@/state'
import EventBus from '@/plugins/event-bus'
import { useKeyboardShortcuts } from './composables/useKeyboardShortcuts'

export default {
  name: 'ProductGrid',

  components: {
    ProductTile,
    MsgBox
  },

  props: {
    selectedTable: {
      type: Object,
      default: () => ({})
    },
    editMode: {
      type: Boolean,
      default: false
    },
    cashRegId: {
      type: String,
      required: true
    },
    keyboardShortcutsEnabled: {
      type: Boolean,
      default: false
    },
    showShortcutBadges: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      rows: [],
      rowCount: null,
      colCount: null,
      colspan: null,
      loaded: false,
      productShortcutMap: {}, // Map product shortcuts to grid positions
      categoryKeyBuffer: '',
      categoryKeyTimer: null,
      selectProductDialogOpen: false, // Used by keyboard shortcuts composable
      spaceKeyPressed: false,
      spaceKeyPressTimer: null,
      showBadgesTemporarily: false, // Local state for temporary badge display on Space key
      selectedCells: [],
      savedLayouts: [],
      selectedLayoutId: null,
      rowColOptions: Array.from({ length: 12 }, (v, i) => i + 1), // Numbers 1 to 12
      internalRowCount: null,
      internalColCount: null,
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        icon: 'mdi-check-circle'
      },
      confirmCallback: null // Callback for MsgBox confirmation
    }
  },

  computed: {
    grid () {
      const grid = Array(this.rowCount).fill(null).map(() => Array(this.colCount).fill(null))

      if (this.rows.length > 0) {
        this.rows.forEach(row => {
          row.cols.forEach(cell => {
            if (grid[cell.rowIndex] && grid[cell.rowIndex][cell.colIndex] === null) {
              grid[cell.rowIndex][cell.colIndex] = cell
              const colspan = cell.colspan || 1
              const rowspan = cell.rowspan || 1
              for (let r = 0; r < rowspan; r++) {
                for (let c = 0; c < colspan; c++) {
                  if (r === 0 && c === 0) continue
                  if (grid[cell.rowIndex + r] && grid[cell.rowIndex + r][cell.colIndex + c] === null) {
                    grid[cell.rowIndex + r][cell.colIndex + c] = 'hidden'
                  }
                }
              }
            }
          })
        })
      }
      return grid
    },

    canMerge () {
      if (this.selectedCells.length < 2) {
        return false
      }
      const rows = this.selectedCells.map(cell => cell.rowIndex)
      const cols = this.selectedCells.map(cell => cell.colIndex)
      const minRow = Math.min(...rows)
      const maxRow = Math.max(...rows)
      const minCol = Math.min(...cols)
      const maxCol = Math.max(...cols)
      const expectedCellCount = (maxRow - minRow + 1) * (maxCol - minCol + 1)
      if (this.selectedCells.length !== expectedCellCount) {
        return false
      }
      // check if all cells in the rectangle are selected
      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          if (!this.selectedCells.some(cell => cell.rowIndex === r && cell.colIndex === c)) {
            return false
          }
        }
      }
      return true
    },

    canUnmerge () {
      if (this.selectedCells.length !== 1) {
        return false
      }
      const cell = this.selectedCells[0]
      const item = this.grid[cell.rowIndex][cell.colIndex]
      if (!item) return false
      return (item.colspan && item.colspan > 1) || (item.rowspan && item.rowspan > 1)
    },

    canDelete () {
      if (this.selectedCells.length !== 1) {
        return false
      }
      const cell = this.selectedCells[0]
      const item = this.grid[cell.rowIndex][cell.colIndex]
      if (!item) return false
      // Can delete if the cell is active (has content)
      return item.active === true
    },

    // OPTIMIZATION: Pre-compute all product shortcuts for O(1) lookup
    itemShortcuts () {
      const shortcuts = {}

      // Pre-compute all shortcuts
      Object.entries(this.productShortcutMap).forEach(([key, shortcut]) => {
        shortcuts[key] = shortcut
      })

      return shortcuts
    }
  },

  methods: {
    showToast (message, color = 'success', icon = 'mdi-check-circle') {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.icon = icon
      this.snackbar.show = true
    },

    showConfirm (message, callback) {
      this.confirmCallback = callback
      this.$refs.msgBox.show({
        text: message,
        actions: ['cancel', 'ok'],
        cancelBtnText: 'Odustani',
        okBtnText: 'Potvrdi',
        color: 'warning'
      })
    },

    confirmDialogAction () {
      if (this.confirmCallback) {
        this.confirmCallback()
        this.confirmCallback = null
      }
    },

    onConfirmCancel () {
      this.confirmCallback = null
    },

    isCellHidden (rowIndex, colIndex) {
      const cell = this.grid[rowIndex][colIndex]
      return cell === 'hidden'
    },

    /**
     * Initialize grid with empty cells
     */
    async setup (forceServerRead = false) {
      for (let i = 0; i < this.rowCount; i++) {
        const row = {
          rowIndex: i,
          cols: []
        }

        for (let j = 0; j < this.colCount; j++) {
          row.cols.push({
            active: false,
            rowIndex: i,
            colIndex: j,
            color: '#f5f5f5', // Default light gray color for empty tiles
            colspan: 1,
            rowspan: 1,
            id: '',
            parentId: '',
            label: '',
            type: '',
            foregroundColor: 'white',
            hasGlassEffect: false,
            has3DEffect: false,
            hasNeonEffect: false,
            hasPulseEffect: false,
            hasNeumorphismEffect: false,
            hasGradientBorderEffect: false,
            hasShimmerEffect: false,
            hasFloatingEffect: false,
            hasShadowLiftEffect: false,
            hasHolographicEffect: false,
            hasFrostEffect: false,
            hasBorderGlowEffect: false,
            hasBreathingEffect: false
          })
        }

        this.rows.push(row)
      }
      // Wait for data to populate before assigning shortcuts
      await this.populateData(forceServerRead)
      // Assign product shortcuts after setup
      this.$nextTick(() => {
        this.keyboardShortcuts.assignProductShortcuts()
      })
    },

    /**
     * Populate grid data from Firebase
     */
    async populateData (forceServerRead = false) {
      const query = df.collection(`cash_registers/${this.cashRegId}/gui_configuration_rows`)

      try {
        const options = forceServerRead ? { source: 'server' } : {}
        const doc = await query.get(options)

        // Iterate through ALL Firebase documents, not just up to rowCount
        doc.docs.forEach(docSnapshot => {
          const row = docSnapshot.data()

          if (row.cols && Array.isArray(row.cols)) {
            row.cols.forEach(tile => {
              const newTile = {
                active: tile.active,
                color: tile.color,
                colspan: tile.colspan,
                rowspan: tile.rowspan,
                id: tile.id,
                label: tile.label,
                type: tile.type,
                foregroundColor: tile.foregroundColor,
                shortcut: tile.shortcut,
                hasGlassEffect: tile.hasGlassEffect || false,
                has3DEffect: tile.has3DEffect || false,
                hasNeonEffect: tile.hasNeonEffect || false,
                hasPulseEffect: tile.hasPulseEffect || false,
                hasNeumorphismEffect: tile.hasNeumorphismEffect || false,
                hasGradientBorderEffect: tile.hasGradientBorderEffect || false,
                hasShimmerEffect: tile.hasShimmerEffect || false,
                hasFloatingEffect: tile.hasFloatingEffect || false,
                hasShadowLiftEffect: tile.hasShadowLiftEffect || false,
                hasHolographicEffect: tile.hasHolographicEffect || false,
                hasFrostEffect: tile.hasFrostEffect || false,
                hasBorderGlowEffect: tile.hasBorderGlowEffect || false,
                hasBreathingEffect: tile.hasBreathingEffect || false,
                // Converted properties
                rowIndex: tile.row_index,
                colIndex: tile.col_index,
                parentId: tile.parent_id,
                mergedInto: tile.merged_into
              }
              // Only add tile if it's within current grid boundaries
              if (newTile.rowIndex < this.rowCount &&
                  newTile.colIndex < this.colCount &&
                  this.rows[newTile.rowIndex] &&
                  this.rows[newTile.rowIndex].cols[newTile.colIndex]) {
                // Place tile in grid at its stored position
                this.rows[newTile.rowIndex].cols[newTile.colIndex] = newTile
              }
            })
          }
        })

        // Assign shortcuts to products in grid
        this.keyboardShortcuts.assignProductShortcuts()
        this.loaded = true
      } catch (error) {
        console.error('Error fetching grid data: ', error)
      }
    },

    /**
     * Handle tile click
     */
    onCellClick (item) {
      if (this.editMode) {
        this.toggleCellSelection(item)
      } else {
        // Clear category key buffer when clicking
        this.categoryKeyBuffer = ''
        if (this.categoryKeyTimer) {
          clearTimeout(this.categoryKeyTimer)
        }

        if (item.type === 'category') {
          this.$emit('add-category', item)
        } else if (item.type === 'product' || item.type === 'menu-product') {
          this.$emit('add-product', item)
        }
      }
    },

    toggleCellSelection (item) {
      const { rowIndex, colIndex } = item
      const index = this.selectedCells.findIndex(
        cell => cell.rowIndex === rowIndex && cell.colIndex === colIndex
      )
      if (index > -1) {
        this.selectedCells.splice(index, 1)
      } else {
        this.selectedCells.push({ rowIndex, colIndex })
      }
    },

    isCellSelected (item) {
      if (!item) return false
      const { rowIndex, colIndex } = item
      return this.selectedCells.some(
        cell => cell.rowIndex === rowIndex && cell.colIndex === colIndex
      )
    },

    mergeCells () {
      if (!this.canMerge) return

      const rows = this.selectedCells.map(cell => cell.rowIndex)
      const cols = this.selectedCells.map(cell => cell.colIndex)
      const minRow = Math.min(...rows)
      const maxRow = Math.max(...rows)
      const minCol = Math.min(...cols)
      const maxCol = Math.max(...cols)

      const mainCell = this.rows[minRow].cols[minCol]
      const updatedMainCell = {
        ...mainCell,
        colspan: maxCol - minCol + 1,
        rowspan: maxRow - minRow + 1
      }
      this.$set(this.rows[minRow].cols, minCol, updatedMainCell)

      for (let r = minRow; r <= maxRow; r++) {
        for (let c = minCol; c <= maxCol; c++) {
          if (r === minRow && c === minCol) continue
          const cell = this.rows[r].cols[c]
          if (typeof cell === 'object' && cell !== null) {
            const updatedCell = {
              ...cell,
              active: false,
              mergedInto: { row: minRow, col: minCol }
            }
            this.$set(this.rows[r].cols, c, updatedCell)
          }
        }
      }

      this.updateGridInFirebase()
      this.selectedCells = []
    },

    unmergeCell () {
      if (!this.canUnmerge) return

      const cellCoord = this.selectedCells[0]
      const mainCell = this.rows[cellCoord.rowIndex].cols[cellCoord.colIndex]

      const { rowspan, colspan } = mainCell

      const updatedMainCell = {
        ...mainCell,
        rowspan: 1,
        colspan: 1
      }
      this.$set(this.rows[cellCoord.rowIndex].cols, cellCoord.colIndex, updatedMainCell)

      for (let r = cellCoord.rowIndex; r < cellCoord.rowIndex + (rowspan || 1); r++) {
        for (let c = cellCoord.colIndex; c < cellCoord.colIndex + (colspan || 1); c++) {
          if (r === cellCoord.rowIndex && c === cellCoord.colIndex) continue
          const mergedCell = this.rows[r].cols[c]
          if (mergedCell && mergedCell.mergedInto && mergedCell.mergedInto.row === cellCoord.rowIndex && mergedCell.mergedInto.col === cellCoord.colIndex) {
            const updatedCell = {
              ...mergedCell,
              active: true,
              mergedInto: null
            }
            this.$set(this.rows[r].cols, c, updatedCell)
          }
        }
      }

      this.updateGridInFirebase()
      this.selectedCells = []
    },

    editCell () {
      if (this.selectedCells.length !== 1) return

      const { rowIndex, colIndex } = this.selectedCells[0]
      const item = this.grid[rowIndex][colIndex]
      const itemToSend = { ...item }

      if (item.active) {
        this.$emit('open-edit-dialog', itemToSend)
      } else {
        this.$emit('open-add-dialog', itemToSend)
      }
    },

    async deleteCell () {
      if (this.selectedCells.length !== 1) return

      const { rowIndex, colIndex } = this.selectedCells[0]
      const item = this.grid[rowIndex][colIndex]

      if (!item || !item.active) {
        return
      }

      // Call onDeleteItem to handle the deletion
      await this.onDeleteItem({ rowIndex, colIndex })

      // Clear selection after deletion
      this.selectedCells = []
    },

    async saveAndExitEditMode () {
      // Clear any selected cells
      this.selectedCells = []

      // Emit event to exit edit mode.
      // The parent component is expected to handle the primary save to gui_configuration_rows.
      EventBus.$emit('set-mode', false)
    },

    /**
     * Add item to panel (called from parent via dialog)
     */
    addItemToPanel (id, categoryId, colspan, name, backgroundColor, foregroundColor, type, selectedTile, effects = {}) {
      if (!selectedTile) {
        console.error('addItemToPanel: selectedTile is null')
        return
      }

      // Check if rows array and row exists
      if (!this.rows || !this.rows[selectedTile.rowIndex]) {
        console.error('addItemToPanel: rows array not initialized or row does not exist', {
          rows: this.rows,
          rowIndex: selectedTile.rowIndex
        })
        return
      }

      const isMerged = (selectedTile.colspan && selectedTile.colspan > 1) || (selectedTile.rowspan && selectedTile.rowspan > 1)
      const newColspan = isMerged ? selectedTile.colspan : (colspan || 1)
      const newRowspan = isMerged ? selectedTile.rowspan : 1

      // Update the tile in the grid's rows array using Vue.set for reactivity
      this.$set(this.rows[selectedTile.rowIndex].cols, selectedTile.colIndex, {
        active: true,
        rowIndex: selectedTile.rowIndex,
        colIndex: selectedTile.colIndex,
        color: backgroundColor,
        foregroundColor: foregroundColor,
        colspan: newColspan,
        rowspan: newRowspan,
        id: id,
        parentId: categoryId,
        label: name,
        type: type,
        hasGlassEffect: effects.hasGlassEffect || false,
        has3DEffect: effects.has3DEffect || false,
        hasNeonEffect: effects.hasNeonEffect || false,
        hasPulseEffect: effects.hasPulseEffect || false,
        hasNeumorphismEffect: effects.hasNeumorphismEffect || false,
        hasGradientBorderEffect: effects.hasGradientBorderEffect || false,
        hasShimmerEffect: effects.hasShimmerEffect || false,
        hasFloatingEffect: effects.hasFloatingEffect || false,
        hasShadowLiftEffect: effects.hasShadowLiftEffect || false,
        hasHolographicEffect: effects.hasHolographicEffect || false,
        hasFrostEffect: effects.hasFrostEffect || false,
        hasBorderGlowEffect: effects.hasBorderGlowEffect || false,
        hasBreathingEffect: effects.hasBreathingEffect || false
      })

      this.$emit('tile-updated', selectedTile)

      // Clear selection after adding item
      this.selectedCells = []

      // Reassign shortcuts after adding/updating item
      this.$nextTick(() => {
        if (this.keyboardShortcuts) {
          this.keyboardShortcuts.assignProductShortcuts()
        }
      })
    },

    /**
     * Delete tile from grid
     */
    async onDeleteItem (item) {
      // Support both snake_case and camelCase property names
      const rowIndex = item.rowIndex ?? item.row_index
      const colIndex = item.colIndex ?? item.col_index

      if (rowIndex === undefined || colIndex === undefined) {
        console.error('onDeleteItem: invalid item indices', { item })
        return
      }

      if (!this.rows[rowIndex] || !this.rows[rowIndex].cols[colIndex]) {
        console.error('onDeleteItem: tile not found', { rowIndex, colIndex })
        return
      }

      const tile = this.rows[rowIndex].cols[colIndex]
      const rowspan = tile.rowspan || 1
      const colspan = tile.colspan || 1

      // Reset the main tile
      tile.active = false
      tile.color = '#f5f5f5' // Default light gray color for empty tiles
      tile.colspan = 1
      tile.rowspan = 1
      tile.id = ''
      tile.label = ''
      tile.type = ''
      tile.parentId = ''
      tile.mergedInto = null
      tile.foregroundColor = 'white'
      tile.hasGlassEffect = false
      tile.has3DEffect = false
      tile.hasNeonEffect = false
      tile.hasPulseEffect = false
      tile.hasNeumorphismEffect = false
      tile.hasGradientBorderEffect = false
      tile.hasShimmerEffect = false
      tile.hasFloatingEffect = false
      tile.hasShadowLiftEffect = false
      tile.hasHolographicEffect = false
      tile.hasFrostEffect = false
      tile.hasBorderGlowEffect = false
      tile.hasBreathingEffect = false

      // Reset all merged cells that were part of this tile
      for (let r = rowIndex; r < rowIndex + rowspan; r++) {
        for (let c = colIndex; c < colIndex + colspan; c++) {
          // Skip the main cell (already handled)
          if (r === rowIndex && c === colIndex) continue

          // Check if this row and col exist
          if (this.rows[r] && this.rows[r].cols[c]) {
            const mergedCell = this.rows[r].cols[c]

            // Only reset if this cell was merged into the deleted tile
            if (mergedCell.mergedInto &&
                mergedCell.mergedInto.row === rowIndex &&
                mergedCell.mergedInto.col === colIndex) {
              mergedCell.active = false
              mergedCell.color = '#f5f5f5' // Default light gray color for empty tiles
              mergedCell.colspan = 1
              mergedCell.rowspan = 1
              mergedCell.id = ''
              mergedCell.label = ''
              mergedCell.type = ''
              mergedCell.parentId = ''
              mergedCell.mergedInto = null
              mergedCell.foregroundColor = 'white'
              mergedCell.hasGlassEffect = false
              mergedCell.has3DEffect = false
              mergedCell.hasNeonEffect = false
              mergedCell.hasPulseEffect = false
              mergedCell.hasNeumorphismEffect = false
              mergedCell.hasGradientBorderEffect = false
              mergedCell.hasShimmerEffect = false
              mergedCell.hasFloatingEffect = false
              mergedCell.hasShadowLiftEffect = false
              mergedCell.hasHolographicEffect = false
              mergedCell.hasFrostEffect = false
              mergedCell.hasBorderGlowEffect = false
              mergedCell.hasBreathingEffect = false
            }
          }
        }
      }

      // Force Vue to update the grid display immediately
      this.$forceUpdate()

      await this.updateGridInFirebase()
    },

    async updateGridInFirebase () {
      const batch = df.batch()
      this.rows.forEach(row => {
        const newCols = row.cols.map(col => {
          const newCol = {
            active: col.active,
            color: col.color,
            colspan: col.colspan,
            rowspan: col.rowspan,
            id: col.id,
            label: col.label,
            type: col.type,
            foregroundColor: col.foregroundColor,
            shortcut: col.shortcut,
            hasGlassEffect: col.hasGlassEffect || false,
            has3DEffect: col.has3DEffect || false,
            hasNeonEffect: col.hasNeonEffect || false,
            hasPulseEffect: col.hasPulseEffect || false,
            hasNeumorphismEffect: col.hasNeumorphismEffect || false,
            hasGradientBorderEffect: col.hasGradientBorderEffect || false,
            hasShimmerEffect: col.hasShimmerEffect || false,
            hasFloatingEffect: col.hasFloatingEffect || false,
            hasShadowLiftEffect: col.hasShadowLiftEffect || false,
            hasHolographicEffect: col.hasHolographicEffect || false,
            hasFrostEffect: col.hasFrostEffect || false,
            hasBorderGlowEffect: col.hasBorderGlowEffect || false,
            hasBreathingEffect: col.hasBreathingEffect || false,
            // Converted properties
            row_index: col.rowIndex,
            col_index: col.colIndex,
            parent_id: col.parentId,
            merged_into: col.mergedInto
          }

          // Sanitize object before sending to Firebase
          Object.keys(newCol).forEach(key => {
            if (newCol[key] === undefined) {
              delete newCol[key]
            }
          })

          return newCol
        })
        const rowRef = df.doc(`cash_registers/${this.cashRegId}/gui_configuration_rows/row${row.rowIndex}`)
        // Use set with merge instead of update to handle non-existent documents
        batch.set(rowRef, { cols: newCols }, { merge: true })
      })
      await batch.commit()
      // this.reload()
    },

    /**
     * Get panel parameters from Firebase
     */
    async getPanelParams (forceServerRead = false) {
      const options = forceServerRead ? { source: 'server' } : {}
      const register = await df.doc(`cash_registers/${this.cashRegId}`).get(options)
      this.rowCount = register.data().panel_rows ? register.data().panel_rows : 6
      this.colCount = register.data().panel_cols ? register.data().panel_cols : 6
      this.colspan = register.data().panel_colspan ? register.data().panel_colspan : 12 / this.colCount

      state.setPanelRows(this.rowCount)
      state.setPanelCols(this.colCount)
      state.setPanelColspan(this.colspan)
    },

    /**
     * Reload grid data
     */
    async reload (forceServerRead = false) {
      this.loaded = false
      this.rows = []
      await this.getPanelParams(forceServerRead)
      await this.setup(forceServerRead)
    },

    /**
     * Get item shortcut (wrapper for composable method)
     */
    getItemShortcut (item) {
      return this.keyboardShortcuts.getItemShortcut(item)
    },

    /**
     * Get category shortcut (wrapper for composable method)
     */
    getCategoryShortcut (item) {
      return this.keyboardShortcuts.getCategoryShortcut(item)
    },

    async fetchSavedLayouts () {
      try {
        const querySnapshot = await df.collection(`cash_registers/${this.cashRegId}/gui_layouts`).orderBy('createdAt', 'desc').get()
        this.savedLayouts = querySnapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name
        }))
      } catch (error) {
        console.error('Error fetching saved layouts:', error)
      }
    },

    async saveLayoutAs () {
      const name = prompt('Unesite ime za ovaj prikaz:')
      if (!name) return

      try {
        const layoutData = {}
        this.rows.forEach(row => {
          const newCols = row.cols.map(col => {
            const newCol = {
              active: col.active,
              color: col.color,
              colspan: col.colspan,
              rowspan: col.rowspan,
              id: col.id,
              label: col.label,
              type: col.type,
              foregroundColor: col.foregroundColor,
              shortcut: col.shortcut,
              hasGlassEffect: col.hasGlassEffect || false,
              has3DEffect: col.has3DEffect || false,
              hasNeonEffect: col.hasNeonEffect || false,
              hasPulseEffect: col.hasPulseEffect || false,
              hasNeumorphismEffect: col.hasNeumorphismEffect || false,
              hasGradientBorderEffect: col.hasGradientBorderEffect || false,
              hasShimmerEffect: col.hasShimmerEffect || false,
              hasFloatingEffect: col.hasFloatingEffect || false,
              hasShadowLiftEffect: col.hasShadowLiftEffect || false,
              hasHolographicEffect: col.hasHolographicEffect || false,
              hasFrostEffect: col.hasFrostEffect || false,
              hasBorderGlowEffect: col.hasBorderGlowEffect || false,
              hasBreathingEffect: col.hasBreathingEffect || false,
              // Converted properties
              row_index: col.rowIndex,
              col_index: col.colIndex,
              parent_id: col.parentId,
              merged_into: col.mergedInto
            }

            // Sanitize object before sending to Firebase
            Object.keys(newCol).forEach(key => {
              if (newCol[key] === undefined) {
                delete newCol[key]
              }
            })

            return newCol
          })
          layoutData[`row${row.rowIndex}`] = { cols: newCols }
        })

        await df.collection(`cash_registers/${this.cashRegId}/gui_layouts`).add({
          name: name,
          createdAt: new Date(),
          panel_rows: this.rowCount,
          panel_cols: this.colCount,
          panel_colspan: this.colspan,
          layout_data: layoutData
        })

        await this.fetchSavedLayouts()
        this.showToast('Prikaz je spremljen.', 'success', 'mdi-check-circle')
      } catch (error) {
        console.error('Error saving layout:', error)
        this.showToast('Greška pri spremanju prikaza.', 'error', 'mdi-alert-circle')
      }
    },

    loadLayout () {
      if (!this.selectedLayoutId) return

      this.showConfirm(
        'Jeste li sigurni? Trenutni nespremljeni prikaz će biti prepisan.',
        () => this.executeLoadLayout()
      )
    },

    async executeLoadLayout () {
      try {
        this.loaded = false
        // 1. Fetch the saved layout from gui_layouts
        const layoutDoc = await df.doc(`cash_registers/${this.cashRegId}/gui_layouts/${this.selectedLayoutId}`).get()
        if (!layoutDoc.exists) {
          this.showToast('Odabrani prikaz ne postoji.', 'error', 'mdi-alert-circle')
          this.loaded = true
          return
        }

        const layoutDocData = layoutDoc.data()
        const layoutData = layoutDocData.layout_data
        const newRowCount = layoutDocData.panel_rows
        const newColCount = layoutDocData.panel_cols
        const oldRowCount = this.rowCount

        // 2. Start a batch write
        const batch = df.batch()

        // 3. Update the cash register document with the new panel dimensions
        const registerRef = df.doc(`cash_registers/${this.cashRegId}`)
        batch.update(registerRef, {
          panel_rows: newRowCount,
          panel_cols: newColCount,
          panel_colspan: layoutDocData.panel_colspan
        })

        // 4. Overwrite/delete rows in gui_configuration_rows
        const maxRows = Math.max(oldRowCount, newRowCount)
        for (let i = 0; i < maxRows; i++) {
          const rowRef = df.doc(`cash_registers/${this.cashRegId}/gui_configuration_rows/row${i}`)
          if (i < newRowCount) {
            // This row should exist, set its data
            const rowData = layoutData[`row${i}`] || {
              cols: Array.from({ length: newColCount }, (v, j) => ({
                active: false,
                row_index: i,
                col_index: j,
                color: '#f5f5f5', // Default light gray color for empty tiles
                colspan: 1,
                rowspan: 1,
                id: '',
                parent_id: '',
                label: '',
                type: '',
                foregroundColor: 'white'
              }))
            }
            batch.set(rowRef, rowData)
          } else {
            // This row is extra and should be deleted
            batch.delete(rowRef)
          }
        }

        // 5. Commit all changes to Firebase
        await batch.commit()

        // 6. Reload the grid from Firebase. The reload will now fetch the new panel params.
        await this.reload(true)

        this.showToast('Prikaz je učitan.', 'success', 'mdi-check-circle')
      } catch (error) {
        console.error('Error loading layout:', error)
        this.showToast('Greška pri učitavanju prikaza.', 'error', 'mdi-alert-circle')
        this.loaded = true
      }
    },

    deleteLayout () {
      if (!this.selectedLayoutId) return

      this.showConfirm(
        'Jeste li sigurni da želite obrisati ovaj spremljeni prikaz?',
        () => this.executeDeleteLayout()
      )
    },

    async executeDeleteLayout () {
      try {
        await df.doc(`cash_registers/${this.cashRegId}/gui_layouts/${this.selectedLayoutId}`).delete()
        this.selectedLayoutId = null
        await this.fetchSavedLayouts()
        this.showToast('Prikaz je obrisan.', 'success', 'mdi-check-circle')
      } catch (error) {
        console.error('Error deleting layout:', error)
        this.showToast('Greška pri brisanju prikaza.', 'error', 'mdi-alert-circle')
      }
    },

    async updateGridDimensions () {
      try {
        // Update the main cash register document with the new panel dimensions
        const registerRef = df.doc(`cash_registers/${this.cashRegId}`)
        await registerRef.update({
          panel_rows: this.internalRowCount,
          panel_cols: this.internalColCount,
          panel_colspan: 12 / this.internalColCount // Recalculate colspan
        })

        // Reload the grid with forced server read to reflect new dimensions
        await this.reload(true)

        this.showToast('Dimenzije rešetke su ažurirane.', 'success', 'mdi-check-circle')
      } catch (error) {
        console.error('Error updating grid dimensions:', error)
        this.showToast('Greška pri ažuriranju dimenzija rešetke.', 'error', 'mdi-alert-circle')
      }
    }
  },

  async mounted () {
    // Initialize keyboard shortcuts composable
    this.keyboardShortcuts = useKeyboardShortcuts(this)

    // Register keyboard event listeners
    window.addEventListener('keydown', this.keyboardShortcuts.throttledCategoryKeyPress)
    window.addEventListener('keydown', this.keyboardShortcuts.handleSpaceKeyDown)
    window.addEventListener('keyup', this.keyboardShortcuts.handleSpaceKeyUp)

    // Listen for template adjustments (rows/cols changes)
    EventBus.$off('adjust-template')
    EventBus.$on('adjust-template', async () => {
      this.loaded = false
      this.rowCount = state.getPanelRows()
      this.colCount = state.getPanelCols()
      this.colspan = state.getPanelColspan()
      this.rows = []
      await this.setup()
    })

    // Get panel configuration and setup grid
    await this.getPanelParams()
    await this.setup()
    this.internalRowCount = this.rowCount
    this.internalColCount = this.colCount
    await this.fetchSavedLayouts()
  },

  beforeDestroy () {
    // Remove keyboard event listeners
    if (this.keyboardShortcuts) {
      window.removeEventListener('keydown', this.keyboardShortcuts.throttledCategoryKeyPress)
      window.removeEventListener('keydown', this.keyboardShortcuts.handleSpaceKeyDown)
      window.removeEventListener('keyup', this.keyboardShortcuts.handleSpaceKeyUp)

      // Cleanup keyboard shortcuts timers
      if (this.keyboardShortcuts.cleanup) {
        this.keyboardShortcuts.cleanup()
      }
    }

    // Remove EventBus listener
    EventBus.$off('adjust-template')

    // Clear timers
    if (this.categoryKeyTimer) {
      clearTimeout(this.categoryKeyTimer)
    }
    if (this.spaceKeyPressTimer) {
      clearTimeout(this.spaceKeyPressTimer)
    }
  }
}
</script>

<style scoped>
.product-grid {
  width: 100%;
}

.product-grid-container {
  display: grid;
  gap: 2px;
}

.panel-row {
  margin: 0;
  padding: 0;
}

.panel-cell {
  padding: 1px;
  background-color: white;
  border: 1px;
}

.selected-cell {
  border: 3px solid #3989C9 !important;
  box-shadow: 0 0 0 2px rgba(57, 137, 201, 0.3) !important;
}

/* Edit Toolbar - Glass Morphism Style */
.edit-controls-wrapper {
  position: relative;
  z-index: 10;
  margin-bottom: 12px;
}

.edit-toolbar {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.08) 0%,
    rgba(57, 137, 201, 0.06) 50%,
    rgba(97, 84, 164, 0.08) 100%) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  padding: 10px 16px;
  box-shadow: 0 2px 12px rgba(0, 179, 190, 0.15);
  border-bottom: 2px solid rgba(0, 179, 190, 0.25);
  border-radius: 8px;
}

/* Toolbar Content Container */
.toolbar-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

/* Toolbar Section */
.toolbar-section {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* Section Labels */
.section-label {
  font-size: 12px !important;
  font-weight: 600 !important;
  color: rgba(57, 137, 201, 0.9) !important;
  letter-spacing: 0.3px !important;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Toolbar Spacer */
.toolbar-spacer {
  flex-grow: 1;
}

/* Toolbar Divider */
.toolbar-divider {
  height: 36px !important;
  border-color: rgba(0, 179, 190, 0.25) !important;
  margin: 0 8px !important;
  flex-shrink: 0;
}

/* Filter Select Inputs (Dropdowns) */
.filter-select.v-text-field--outlined >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

.filter-select.v-text-field--outlined.v-input--is-focused >>> fieldset {
  border-color: #3989C9 !important;
  border-width: 2px !important;
}

.filter-select >>> .v-input__slot {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  min-height: 36px !important;
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-input__control {
  border-radius: 8px !important;
}

.filter-select.v-text-field--outlined >>> .v-text-field__slot {
  border-radius: 8px !important;
}

.filter-select >>> .v-select__selection,
.filter-select >>> .v-text-field__slot input {
  font-size: 13px !important;
  font-weight: 500 !important;
  color: rgba(70, 70, 80, 0.9) !important;
  letter-spacing: 0.3px !important;
}

.filter-select >>> label {
  font-size: 12px !important;
  font-weight: 500 !important;
  color: rgba(57, 137, 201, 0.8) !important;
}

/* Dimension Select - Narrow Width */
.dimension-select {
  max-width: 85px !important;
  min-width: 75px !important;
}

/* Layout Select - Narrower Width */
.layout-select {
  max-width: 150px !important;
  min-width: 130px !important;
}

/* Filter Select Dropdown Menu */
.filter-select >>> .v-menu__content {
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(20px) !important;
  border-radius: 8px !important;
  border: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 0 8px 24px rgba(0, 179, 190, 0.12) !important;
}

.filter-select >>> .v-list-item {
  font-size: 13px !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
}

.filter-select >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.filter-select >>> .v-list-item--active {
  background: rgba(0, 179, 190, 0.12) !important;
  color: #3989C9 !important;
}

/* Edit Toolbar Buttons - Soft Style */
.edit-toolbar-btn {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #3989C9 !important;
  border-radius: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.25) !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 0 12px !important;
  height: 36px !important;
  transition: all 0.2s ease !important;
}

.edit-toolbar-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #3989C9 !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.2) !important;
  transform: translateY(-1px);
}

.edit-toolbar-btn:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: rgba(245, 245, 245, 0.8) !important;
  border-color: rgba(200, 200, 200, 0.3) !important;
  color: rgba(158, 158, 158, 0.6) !important;
}

.edit-toolbar-btn .v-icon {
  color: #3989C9 !important;
  font-size: 18px !important;
}

.edit-toolbar-btn:disabled .v-icon {
  color: rgba(158, 158, 158, 0.6) !important;
}

/* Danger Button Style */
.edit-toolbar-btn-danger {
  background: rgba(244, 67, 54, 0.9) !important;
  color: white !important;
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 2px 6px rgba(244, 67, 54, 0.25) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 0 12px !important;
  height: 36px !important;
  transition: all 0.2s ease !important;
}

.edit-toolbar-btn-danger:hover:not(:disabled) {
  background: rgba(244, 67, 54, 1) !important;
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 4px 12px rgba(244, 67, 54, 0.4) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.edit-toolbar-btn-danger:disabled {
  opacity: 0.5 !important;
  cursor: not-allowed !important;
  background: rgba(200, 200, 200, 0.5) !important;
  border-color: rgba(150, 150, 150, 0.3) !important;
}

.edit-toolbar-btn-danger .v-icon {
  color: white !important;
  font-size: 18px !important;
}

/* Primary Action Button (Spremi) */
.edit-toolbar-btn-primary {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.9) 50%,
    rgba(97, 84, 164, 0.95) 100%) !important;
  color: white !important;
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  box-shadow: 0 2px 8px rgba(0, 179, 190, 0.3) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  font-size: 13px !important;
  letter-spacing: 0.3px !important;
  padding: 0 14px !important;
  height: 36px !important;
  transition: all 0.2s ease !important;
}

.edit-toolbar-btn-primary:hover {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 1) 0%,
    rgba(57, 137, 201, 0.95) 50%,
    rgba(97, 84, 164, 1) 100%) !important;
  transform: translateY(-1px) scale(1.02);
  box-shadow: 0 4px 16px rgba(0, 179, 190, 0.5) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

.edit-toolbar-btn-primary .v-icon {
  color: white !important;
  font-size: 18px !important;
}

/* Icon styling in buttons */
.edit-toolbar-btn .v-icon,
.edit-toolbar-btn-danger .v-icon,
.edit-toolbar-btn-primary .v-icon {
  transition: transform 0.2s ease !important;
}

.edit-toolbar-btn:hover:not(:disabled) .v-icon,
.edit-toolbar-btn-danger:hover:not(:disabled) .v-icon,
.edit-toolbar-btn-primary:hover .v-icon {
  transform: scale(1.1);
}

/* Smooth animations for dropdown icons */
.filter-select >>> .v-icon {
  transition: transform 0.2s ease !important;
}

.filter-select.v-input--is-focused >>> .v-icon {
  transform: rotate(180deg);
}

/* Loading state for dimension changes */
.product-grid-container {
  transition: opacity 0.2s ease;
}

/* Empty cell styling in edit mode */
.panel-cell:has(.v-btn[disabled]) {
  opacity: 0.6;
}

.label {
  color: white;
}

/* Responsive adjustments */
@media only screen and (max-width: 1600px) {
  .section-label {
    display: none;
  }

  .toolbar-content {
    gap: 12px;
  }

  .toolbar-section {
    gap: 6px;
  }
}

@media only screen and (max-width: 1400px) {
  .edit-toolbar {
    padding: 10px 12px;
  }

  .dimension-select {
    max-width: 85px !important;
    min-width: 75px !important;
  }

  .layout-select {
    max-width: 160px !important;
    min-width: 140px !important;
  }

  .edit-toolbar-btn,
  .edit-toolbar-btn-danger,
  .edit-toolbar-btn-primary {
    padding: 0 12px !important;
    font-size: 12px !important;
  }

  .toolbar-content {
    gap: 10px;
  }

  .toolbar-section {
    gap: 5px;
  }
}

@media only screen and (max-width: 1200px) {
  .edit-toolbar {
    padding: 8px 10px;
  }

  .toolbar-divider {
    display: none !important;
  }

  .edit-controls-wrapper {
    margin-bottom: 12px;
  }

  .toolbar-content {
    flex-wrap: wrap;
    gap: 8px;
  }

  .toolbar-spacer {
    display: none;
  }
}

@media only screen and (max-height: 899px) {
  .label {
    font-size: 0.9rem;
    font-weight: 700;
  }

  .edit-toolbar {
    padding: 8px 12px;
  }
}

@media only screen and (min-height: 900px) {
  .label {
    font-size: 0.9rem;
    font-weight: 700;
  }
}

/* Visual Effects for Tiles */

/* Glass Effect */
.tile-glass-effect {
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  position: relative;
  overflow: hidden;
}

.tile-glass-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  pointer-events: none;
  z-index: 1;
}

.tile-glass-effect::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.4) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: glass-shine 3s infinite;
  pointer-events: none;
  z-index: 2;
}

@keyframes glass-shine {
  0% {
    left: -100%;
  }
  50%, 100% {
    left: 100%;
  }
}

/* 3D Effect */
.tile-3d-effect {
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.12),
    0 4px 8px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.08),
    inset 0 -2px 4px rgba(0, 0, 0, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 0.3) !important;
  transform: translateZ(0);
  position: relative;
}

.tile-3d-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 30%;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.3) 0%,
    rgba(255, 255, 255, 0) 100%
  );
  border-radius: 12px 12px 0 0;
  pointer-events: none;
  z-index: 1;
}

/* Combined Effects */
.tile-glass-effect.tile-3d-effect {
  backdrop-filter: blur(10px) !important;
  -webkit-backdrop-filter: blur(10px) !important;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 6px 12px rgba(0, 0, 0, 0.12),
    0 12px 24px rgba(0, 0, 0, 0.1),
    inset 0 -2px 4px rgba(0, 0, 0, 0.1),
    inset 0 2px 4px rgba(255, 255, 255, 0.4) !important;
}

/* Neon/Glow Effect */
.tile-neon-effect {
  box-shadow:
    0 0 5px currentColor,
    0 0 10px currentColor,
    0 0 20px currentColor,
    0 0 40px currentColor !important;
  border: 2px solid rgba(255, 255, 255, 0.5) !important;
  filter: brightness(1.1);
}

/* Pulse Effect */
.tile-pulse-effect {
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.02);
    opacity: 0.95;
  }
}

/* Neumorphism Effect */
.tile-neumorphism-effect {
  box-shadow:
    8px 8px 16px rgba(0, 0, 0, 0.15),
    -8px -8px 16px rgba(255, 255, 255, 0.7) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
}

/* Gradient Border Effect */
.tile-gradient-border-effect {
  position: relative;
  border: 3px solid transparent !important;
  background-clip: padding-box;
}

.tile-gradient-border-effect::before {
  content: '';
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  background: linear-gradient(
    45deg,
    #ff6b6b,
    #4ecdc4,
    #45b7d1,
    #f7dc6f,
    #ff6b6b
  );
  border-radius: 12px;
  z-index: -1;
  animation: gradient-rotate 4s linear infinite;
  background-size: 300% 300%;
}

@keyframes gradient-rotate {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Shimmer/Sparkle Effect */
.tile-shimmer-effect {
  position: relative;
  overflow: hidden;
}

.tile-shimmer-effect::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.8) 0%,
    rgba(255, 255, 255, 0.4) 20%,
    transparent 40%
  );
  animation: shimmer 3s infinite;
  pointer-events: none;
  z-index: 10;
}

@keyframes shimmer {
  0% {
    transform: translate(-100%, -100%) rotate(0deg);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translate(100%, 100%) rotate(360deg);
    opacity: 0;
  }
}

/* Floating Effect */
.tile-floating-effect {
  animation: floating 3s ease-in-out infinite;
}

@keyframes floating {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-8px);
  }
}

/* Shadow Lift Effect */
.tile-shadow-lift-effect {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25) !important;
  transform: translateY(-4px);
  transition: all 0.3s ease;
}

.tile-shadow-lift-effect:hover {
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
  transform: translateY(-6px);
}

/* Holographic Effect */
.tile-holographic-effect {
  position: relative;
  overflow: hidden;
  background: linear-gradient(
    45deg,
    #ff0080 0%,
    #ff8c00 15%,
    #40e0d0 30%,
    #00ff00 45%,
    #4169e1 60%,
    #ff0080 75%,
    #ff8c00 100%
  ) !important;
  background-size: 200% 200%;
  animation: holographic 4s linear infinite;
}

.tile-holographic-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.4) 0%,
    transparent 50%,
    rgba(255, 255, 255, 0.3) 100%
  );
  animation: holographic-shimmer 2s ease-in-out infinite;
  pointer-events: none;
}

@keyframes holographic {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

@keyframes holographic-shimmer {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

/* Frost/Ice Effect */
.tile-frost-effect {
  position: relative;
  backdrop-filter: blur(8px) brightness(1.1) !important;
  -webkit-backdrop-filter: blur(8px) brightness(1.1) !important;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.5) 0%,
    rgba(230, 247, 255, 0.4) 50%,
    rgba(200, 235, 255, 0.5) 100%
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.6) !important;
  box-shadow:
    inset 0 1px 2px rgba(255, 255, 255, 0.8),
    inset 0 -1px 2px rgba(0, 0, 0, 0.1),
    0 4px 12px rgba(0, 100, 255, 0.15) !important;
}

.tile-frost-effect::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.4) 50%,
    transparent 70%
  );
  background-size: 200% 200%;
  animation: frost-sparkle 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes frost-sparkle {
  0%, 100% {
    background-position: 0% 0%;
    opacity: 0.5;
  }
  50% {
    background-position: 100% 100%;
    opacity: 0.8;
  }
}

/* Border Glow Effect */
.tile-border-glow-effect {
  border: 2px solid currentColor !important;
  box-shadow:
    0 0 8px currentColor,
    0 0 16px currentColor,
    inset 0 0 8px currentColor !important;
  animation: border-glow-pulse 2s ease-in-out infinite;
}

@keyframes border-glow-pulse {
  0%, 100% {
    box-shadow:
      0 0 8px currentColor,
      0 0 16px currentColor,
      inset 0 0 8px currentColor;
  }
  50% {
    box-shadow:
      0 0 12px currentColor,
      0 0 24px currentColor,
      inset 0 0 12px currentColor;
  }
}

/* Breathing Effect */
.tile-breathing-effect {
  animation: breathing 4s ease-in-out infinite;
}

@keyframes breathing {
  0%, 100% {
    opacity: 0.85;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.01);
  }
}
</style>
